import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  Button,
  IconButton,
  Grid
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

import { DateRangeFilter } from '../../../components/DateRangeFilter';
import { ApplyFilters } from '../../../components/ApplyFilter';
import DownloadIcon from '@mui/icons-material/Download';

import {REFERRAL_KEY_MAP, REFERRAL_SERIES_DROPDOWN } from './constant';
import { LocationFilter } from 'components/LocationFilter';

export default function ReferralDashboard( { rawReferralData}: {rawReferralData: any[]}) {
  const [lineChartData, setLineChartData] = useState<any[]>([]);
  const [location, setLocation] = useState('ALL');
  const [range, setRange] = useState('ALL'); // ✅ default
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedSeries, setSelectedSeries] = useState<string[]> (
      REFERRAL_SERIES_DROPDOWN.map(s => s.key)
  );

useEffect(() => {
  if (rawReferralData?.length) {
    setLineChartData(rawReferralData);
  }
}, [rawReferralData]);



const visibleSeries = useMemo(() => {
  return REFERRAL_SERIES_DROPDOWN
    .filter(item => selectedSeries.includes(item.key))
    .map(item => ({
      dataKey: item.key,
      label: item.label,
      showMark: true,
    }));
}, [selectedSeries]);

function exportToCSV(filename: string, rows: any[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      headers.map(h => `${row[h] ?? 0}`).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function buildExportRows(
  chartData: any[],
  aggregationMode: 'DAILY' | 'MONTHLY' | 'YEARLY' | 'DEFAULT',
  selectedSeries: string[]
) {
 return chartData.map(row => {
    const base: Record<string, any> =
      aggregationMode === 'YEARLY' || aggregationMode === 'DEFAULT'
        ? { Period: row.year }
        : aggregationMode === 'MONTHLY'
        ? { Period: row.month }
        : { Period: row.date };
    selectedSeries.forEach(seriesKey => {
      const seriesLabel = REFERRAL_SERIES_DROPDOWN.find(s => s.key === seriesKey)?.label;
      if (seriesLabel) {
        base[seriesLabel] = row[seriesKey] ?? 0;
      }
    });
    return base;
  });
}

function getDateDiffInDays(from: string, to: string) {
  const start = new Date(from);
  const end = new Date(to);
  return Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function getAggregationMode(range: string, fromDate: string, toDate: string) {
  if ( range === 'ALL' ) {
    return 'DEFAULT';
  }

  if ( range === 'LAST_30_DAYS' || range === 'LAST_60_DAYS') {
    return 'DAILY';
  }

  if ( range === 'LAST_90_DAYS' ) {
    return 'MONTHLY';
  }

  if (range === 'CUSTOM' && fromDate && toDate) {
    const diffDays = getDateDiffInDays(fromDate, toDate);
    return diffDays <= 60 ? 'DAILY' : diffDays  <=365 ? 'MONTHLY' : 'YEARLY';
  }

  return 'MONTHLY'; // fallback (ALL / default)
}

function aggregateYearlyReferralTrend(data: any[]) {
  const map: Record<string, any> = {};
  data.forEach(item => {
    const year = item.createdDate.slice(0, 4);
    const key = REFERRAL_KEY_MAP[item.referral];
    if (!key) return;
    if (!map[year]) {
      map[year] = {
        year,
        google: 0,
        wordOfMouth: 0,
        pcp: 0,
        socialMedia: 0,
        therapist: 0,
        hospital: 0,
        insurance: 0,
        school: 0,
        aiTools: 0,
        migrated: 0,
        accountmgnt: 0,
        onlinevideo: 0,
        other: 0,
      };
    }

    map[year][key] += 1;
  });

  return Object.values(map).sort(
    (a: any, b: any) => Number(a.year) - Number(b.year)
  );
}

function aggregateDailyReferralTrend(data: any[]) {
  const map: Record<string, any> = {};

  data.forEach(item => {
    const date = item.createdDate;
    const key = REFERRAL_KEY_MAP[item.referral];
    if (!key) return;

    if (!map[date]) {
      map[date] = {
        date,
        google: 0,
        wordOfMouth: 0,
        pcp: 0,
        socialMedia: 0,
        therapist: 0,
        hospital: 0,
        insurance: 0,
        school: 0,
        aiTools: 0,
        migrated: 0,
        accountmgnt: 0,
        onlinevideo: 0,
        other: 0,
      };
    }

    map[date][key] += 1;
  });

  return Object.values(map).sort(
    (a: any, b: any) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

function aggregateMonthlyReferralTrend(data: any[]) {
  const map: Record<string, any> = {};

  data.forEach(item => {
    const month = item.createdDate.slice(0, 7); // YYYY-Month
    const key = REFERRAL_KEY_MAP[item.referral];

    if (!key) return;

    if (!map[month]) {
      map[month] = {
        month,
        google: 0,
        wordOfMouth: 0,
        pcp: 0,
        socialMedia: 0,
        therapist: 0,
        hospital: 0,
        insurance: 0,
        school: 0,
        aiTools: 0,
        migrated: 0,
        accountmgnt: 0,
        onlinevideo: 0,
        other: 0,
      };
    }

    map[month][key] += 1;
  });

  return Object.values(map).sort(
    (a: any, b: any) =>
      new Date(a.month).getTime() - new Date(b.month).getTime()
  );
}

const filteredData = useMemo(() => {
  return ApplyFilters(
    lineChartData,
    location,
    range,
    fromDate,
    toDate
  );
}, [lineChartData, location, range, fromDate, toDate]);

const aggregationMode = useMemo(() => {
  return getAggregationMode(range, fromDate, toDate);
}, [range, fromDate, toDate]);

const chartData = useMemo(() => {
if (aggregationMode === 'DEFAULT') {
    return  aggregateYearlyReferralTrend(filteredData);
  }

 if (aggregationMode === 'DAILY') {
    return aggregateDailyReferralTrend(filteredData);
  }

  if (aggregationMode === 'MONTHLY') {
    return aggregateMonthlyReferralTrend(filteredData);
  }

  if (aggregationMode === 'YEARLY') {
    return aggregateYearlyReferralTrend(filteredData);
  }

  return aggregateMonthlyReferralTrend(filteredData);
}, [filteredData, aggregationMode]);


const locations = useMemo(() => {
  return Array.from(
    new Set(
      lineChartData
        .map(item => item.preferredLocation)
        .filter(
          loc => loc && loc.toLowerCase() !== 'marion'
        )
    )
  );
}, [lineChartData]);


return (
    
<Grid container spacing={2} alignItems='center' mb={2} mt={2} >
  {/* Referral Source Multi Select */}
  
  <Grid item xs={12} md={2}>
    <FormControl fullWidth>
      <InputLabel id='referral-select-label'>
        Referral Sources
      </InputLabel>
      <Select
        labelId='referral-select-label'
        multiple
        value={selectedSeries}
        onChange={(e) =>
          setSelectedSeries(
            typeof e.target.value === 'string'
              ? e.target.value.split(',')
              : e.target.value
          )
        }
        input={<OutlinedInput label='Referral Sources' />}
        renderValue={(selected) =>
          REFERRAL_SERIES_DROPDOWN
            .filter(r => selected.includes(r.key))
            .map(r => r.label)
            .join(', ')
        }
      >
        {REFERRAL_SERIES_DROPDOWN.map(item => (
          <MenuItem key={item.key} value={item.key}>
            <Checkbox checked={selectedSeries.includes(item.key)} />
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
  {/* Location */}
  <Grid item xs={12} md={2}>
    {locations && locations.length > 0 ? 
    <LocationFilter
      location={location}
      setLocation={setLocation}
      locations={locations}
    />:null}
  </Grid>

  {/* Date Range */}
  <Grid item xs={12} md={7}>
     {locations && locations.length > 0 ?
    <DateRangeFilter
      range={range}
      setRange={setRange}
      fromDate={fromDate}
      setFromDate={setFromDate}
      toDate={toDate}
      setToDate={setToDate}
    />:null}
  </Grid>

  <Grid item textAlign={'right'}>
{chartData.length > 0 ? (
<IconButton
      color='primary'
      onClick={() => {
        const rows = buildExportRows(
          chartData,
          aggregationMode,
          selectedSeries
        );
        exportToCSV(
          `Referral-Trend-${aggregationMode}-${new Date()
            .toISOString()
            .slice(0, 10)}.csv`,
          rows
        );
      }}
      title='Download CSV'
    > 
      <DownloadIcon />
    </IconButton>
    ):null}
</Grid>

<Grid item xs={12} md={12}>
    {chartData.length > 0 ? (
<LineChart
  margin={{ top: 100, bottom: 70, left: 60, right: 30 }}
  height={500}
  slotProps={{
    legend: {
      direction: 'row',
      position: { vertical: 'top', horizontal: 'middle' },
      padding: 8,
    },
  }}

     xAxis={[
      {
        dataKey:
          aggregationMode === 'YEARLY' || aggregationMode === 'DEFAULT'
            ? 'year'
            : aggregationMode === 'DAILY'
            ? 'date'
            : 'month',
        scaleType: 'band',

label:
  aggregationMode === 'YEARLY' || aggregationMode === 'DEFAULT'
    ? 'Year'
    : aggregationMode === 'DAILY'
    ? range === 'CUSTOM'
      ? `Date range (${fromDate} - ${toDate})`
      : range === 'LAST_30_DAYS'
      ? 'Last 30 Days'
      : range === 'LAST_60_DAYS'
      ? 'Last 60 Days'
      : 'Date'
    : aggregationMode === 'MONTHLY'
    ? range === 'LAST_90_DAYS'
      ? 'Last 90 Days (Monthly)'
      : 'Month'
    : 'Date',
  
            valueFormatter: (value: string) => {
            if (aggregationMode === 'MONTHLY') {
            const [year, month] = value.split('-');
            const date = new Date(Number(year), Number(month) - 1);
            return `${year}-${date.toLocaleString('en-US', { month: 'short' })}`;
            } else if (aggregationMode === 'DAILY') {
              if(range === 'LAST_30_DAYS') {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else if (range === 'LAST_60_DAYS') {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else if (range === 'CUSTOM') {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else {
                return value;
              } 
            } else  {
            return value;
            }
        }
      }
    ]}
    dataset={chartData}
    series={visibleSeries}
  />
): null}
</Grid>

<Grid item xs={12} md={12}>
 {chartData.length === 0 ? (
  <Box textAlign='center' py={6}>
    <Typography color='text.secondary'>
      No data available for selected filters
    </Typography>
  </Box>
):null}
</Grid>
  </Grid>
  );
}